import React, { useMemo, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Table, Button, Modal, Form, Badge, Alert } from "react-bootstrap";
import toast from "react-hot-toast";
import PageTitle from "@/common/PageTitle/PageTitle";
import {
  resolveCatalogRoleId,
  useArchiveEmployeeRoleMutation,
  useCreateEmployeeRoleMutation,
  useListEmployeeRolesQuery,
  useRestoreEmployeeRoleMutation,
  useUpdateEmployeeRoleMutation,
} from "@/features/employees/employeeRolesApi";
import type { ApiEmployeeRole } from "@/features/employees/employeeRoleTypes";
import RolePermissionsEditor from "@/features/employees/RolePermissionsEditor";
import {
  permissionsFromApiRole,
  permissionsToApiPayload,
  type RolePermissionsConfig,
} from "@/features/employees/employeeRolePermissions";
import { useHasPlatformPower } from "@/contexts/SuperAdminIdentityContext";
import styles from "./employee-roles.module.scss";

type RoleFormState = {
  displayName: string;
  description: string;
  sortOrder: string;
};

const EMPTY_FORM: RoleFormState = {
  displayName: "",
  description: "",
  sortOrder: "",
};

function extractErrorMessage(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object" &&
    "message" in error.data &&
    typeof (error as { data: { message: unknown } }).data.message === "string"
  ) {
    return (error as { data: { message: string } }).data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Request failed";
}

function statusBadge(status: string) {
  if (status === "archived") {
    return <Badge bg="secondary">Archived</Badge>;
  }
  return <Badge bg="success">Active</Badge>;
}

function sortRoles(roles: ApiEmployeeRole[]) {
  return [...roles].sort((a, b) => {
    const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return a.displayName.localeCompare(b.displayName);
  });
}

const EmployeeRolesPage: React.FC = () => {
  const canManageRoles = useHasPlatformPower("manage-employee-roles");
  const { data, isLoading, isError, refetch } = useListEmployeeRolesQuery();
  const [createRole, { isLoading: creating }] = useCreateEmployeeRoleMutation();
  const [updateRole, { isLoading: updating }] = useUpdateEmployeeRoleMutation();
  const [archiveRole, { isLoading: archiving }] = useArchiveEmployeeRoleMutation();
  const [restoreRole, { isLoading: restoring }] = useRestoreEmployeeRoleMutation();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showArchivedSection, setShowArchivedSection] = useState(false);
  const [selectedRole, setSelectedRole] = useState<ApiEmployeeRole | null>(null);
  const [form, setForm] = useState<RoleFormState>(EMPTY_FORM);
  const [permissions, setPermissions] = useState<RolePermissionsConfig | null>(null);

  const activeRoles = useMemo(
    () => sortRoles((data?.roles ?? []).filter((role) => role.status === "active")),
    [data?.roles],
  );

  const archivedRoles = useMemo(
    () => sortRoles((data?.roles ?? []).filter((role) => role.status === "archived")),
    [data?.roles],
  );

  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setShowAddModal(true);
  };

  const openEditModal = (role: ApiEmployeeRole) => {
    setSelectedRole(role);
    setForm({
      displayName: role.displayName,
      description: role.description || "",
      sortOrder: String(role.sortOrder ?? ""),
    });
    setPermissions(permissionsFromApiRole(role));
    setShowEditModal(true);
  };

  const openArchiveModal = (role: ApiEmployeeRole) => {
    setSelectedRole(role);
    setShowArchiveModal(true);
  };

  const openRestoreModal = (role: ApiEmployeeRole) => {
    setSelectedRole(role);
    setShowRestoreModal(true);
  };

  const handleCreate = async () => {
    const displayName = form.displayName.trim();
    if (!displayName) {
      toast.error("Role name is required.");
      return;
    }
    try {
      await createRole({
        displayName,
        description: form.description.trim() || undefined,
        sortOrder: form.sortOrder.trim() ? Number(form.sortOrder) : undefined,
      }).unwrap();
      toast.success("Role created.");
      setShowAddModal(false);
      setForm(EMPTY_FORM);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleUpdate = async () => {
    if (!selectedRole) return;
    const displayName = form.displayName.trim();
    if (!displayName) {
      toast.error("Role name is required.");
      return;
    }
    try {
      await updateRole({
        roleId: resolveCatalogRoleId(selectedRole),
        displayName,
        description: form.description.trim(),
        sortOrder: form.sortOrder.trim()
          ? Number(form.sortOrder)
          : selectedRole.sortOrder,
        ...(permissions ? permissionsToApiPayload(permissions) : {}),
      }).unwrap();
      toast.success("Role updated.");
      setShowEditModal(false);
      setSelectedRole(null);
      setPermissions(null);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleArchive = async () => {
    if (!selectedRole) return;
    try {
      await archiveRole(resolveCatalogRoleId(selectedRole)).unwrap();
      toast.success(`${selectedRole.displayName} archived.`);
      setShowArchiveModal(false);
      setSelectedRole(null);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleRestore = async () => {
    if (!selectedRole) return;
    try {
      await restoreRole(resolveCatalogRoleId(selectedRole)).unwrap();
      toast.success(`${selectedRole.displayName} restored.`);
      setShowRestoreModal(false);
      setSelectedRole(null);
      setShowArchivedSection(false);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  return (
    <Container fluid className="p-4">
      <Card className="shadow-sm">
        <Card.Body>
          <div className={styles.headerRow}>
            <PageTitle title="Employee Roles" />
            {canManageRoles && (
              <Button variant="primary" onClick={openAddModal}>
                Add Role
              </Button>
            )}
          </div>

          <p className={styles.helpText}>
            Manage the role catalog used by employee records. Each role has a permanent Role ID
            (ROLE-NNN) and a Role Key used for authorization and employee assignment. Roles in
            use cannot be archived. Archived roles remain visible on historical employee records
            but are hidden from create/edit dropdowns.
          </p>

          {isError && (
            <Alert variant="danger">
              Failed to load roles.{" "}
              <Button variant="link" className="p-0 align-baseline" onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          )}

          <Table responsive hover className={`mt-3 legacy-row-height-table ${styles.rolesTable}`}>
            <thead>
              <tr>
                <th>Role ID</th>
                <th>Role Key</th>
                <th>Name</th>
                <th>Status</th>
                <th>Employees</th>
                <th>Sort</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7}>Loading roles…</td>
                </tr>
              )}
              {!isLoading && activeRoles.length === 0 && (
                <tr>
                  <td colSpan={7}>No active roles found.</td>
                </tr>
              )}
              {!isLoading &&
                activeRoles.map((role) => (
                  <tr key={resolveCatalogRoleId(role) || role.roleKey}>
                    <td className={styles.referenceCell}>{resolveCatalogRoleId(role) || "—"}</td>
                    <td className={styles.referenceCell}>{role.roleKey}</td>
                    <td className={styles.nameCell}>{role.displayName}</td>
                    <td>{statusBadge(role.status)}</td>
                    <td>{role.employeeCount ?? 0}</td>
                    <td>{role.sortOrder}</td>
                    <td>
                      {canManageRoles ? (
                        <div className={styles.actionButtons}>
                          <Button
                            variant="link"
                            className={styles.actionLink}
                            onClick={() => openEditModal(role)}
                            title="Edit role"
                            aria-label="Edit role"
                          >
                            <i className="bi bi-pencil" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="link"
                            className={styles.archiveLink}
                            onClick={() => openArchiveModal(role)}
                            disabled={role.roleKey === "super-admin"}
                            title="Archive role"
                            aria-label="Archive role"
                          >
                            <i className="bi bi-archive" aria-hidden="true" />
                          </Button>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>

          {!isLoading && archivedRoles.length > 0 && (
            <div className={styles.archivedSection}>
              <Button
                variant="link"
                className={styles.archivedToggle}
                onClick={() => setShowArchivedSection((open) => !open)}
                aria-expanded={showArchivedSection}
              >
                {showArchivedSection
                  ? `Hide archived roles (${archivedRoles.length})`
                  : `Show archived roles (${archivedRoles.length})`}
              </Button>

              {showArchivedSection && (
                <Table
                  responsive
                  hover
                  className={`legacy-row-height-table ${styles.rolesTable}`}
                >
                  <thead>
                    <tr>
                      <th>Role ID</th>
                      <th>Role Key</th>
                      <th>Name</th>
                      <th>Employees</th>
                      <th>Sort</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedRoles.map((role) => (
                      <tr key={resolveCatalogRoleId(role) || role.roleKey}>
                        <td className={styles.referenceCell}>
                          {resolveCatalogRoleId(role) || "—"}
                        </td>
                        <td className={styles.referenceCell}>{role.roleKey}</td>
                        <td className={styles.nameCell}>{role.displayName}</td>
                        <td>{role.employeeCount ?? 0}</td>
                        <td>{role.sortOrder}</td>
                        <td>
                          {canManageRoles ? (
                            <Button
                              variant="link"
                              className={styles.restoreLink}
                              onClick={() => openRestoreModal(role)}
                              title="Restore role"
                              aria-label="Restore role"
                            >
                              <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                            </Button>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Role Name</Form.Label>
            <Form.Control
              value={form.displayName}
              onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
              placeholder="e.g. Programming"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="What this role is responsible for"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Sort Order (optional)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={creating} onClick={handleCreate}>
            Create Role
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setPermissions(null);
        }}
        size="lg"
        scrollable
        className={styles.editRoleModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRole && permissions && (
            <>
              <p className={`text-muted mb-3 ${styles.referenceMeta}`}>
                {resolveCatalogRoleId(selectedRole) || "—"} · {selectedRole.roleKey}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Role Name</Form.Label>
                <Form.Control
                  value={form.displayName}
                  onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={form.sortOrder}
                  onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
                  style={{ maxWidth: "120px" }}
                />
              </Form.Group>

              <RolePermissionsEditor value={permissions} onChange={setPermissions} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              setPermissions(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" disabled={updating} onClick={handleUpdate}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Restore Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRole && (
            <Alert variant="info">
              Restore <strong>{selectedRole.displayName}</strong>? It will return to the active
              catalog and appear in employee create/edit dropdowns again.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={restoring} onClick={handleRestore}>
            Restore Role
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Archive Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRole && (
            <>
              <Alert variant="warning">
                Archive <strong>{selectedRole.displayName}</strong>? This removes it from
                employee create/edit dropdowns. Employees already assigned keep their
                historical role. Roles in use cannot be archived.
              </Alert>
              <p className="mb-0">
                Employees currently assigned: <strong>{selectedRole.employeeCount}</strong>
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={archiving || (selectedRole?.employeeCount ?? 0) > 0}
            onClick={handleArchive}
          >
            Archive Role
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(EmployeeRolesPage);
